import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  FormHelperText // Import FormHelperText for standard helper text styling
} from '@mui/material';
import { SmartFormStates } from './SmartForm';
import ErrorWrapper from '@component/ErrorWrapper';
import { syncValidatorResults } from "@component/smartForm/validatorUtils"
import { useTranslation } from "react-i18next";

const SmartSwitchInput = ({
  field,
  label,
  value,
  onChange,
  isEdit,
  sx = {},
  switchSx = {},
  renderView,
  editable = true,
  validator,
  validatorResults,
  setValidatorResults,
  formState,
  helperText, 
  trueValueLabel,
  falseValueLabel,
  ...props
}) => {
  const [showError, setShowError] = useState(false);
  const [validatorResult, setValidatorResult] = useState('');
  const { t } = useTranslation(["main", "shop"]);

  useEffect(() => {
    setShowError(formState === SmartFormStates.SAVING);
  }, [formState]);

  useEffect(() => {
    if (validator) {
      const validatorResult = validator(value);
      syncValidatorResults(validatorResults, setValidatorResults, !validatorResult, label)
      setValidatorResult(validatorResult)
    }
  }, [value, validator, validatorResults, setValidatorResults, label]) // Added dependencies for useEffect

  const handleChange = (e) => {
    onChange(field, e.target.checked);
  };

  return (
    <Box sx={sx}>
      {!isEdit || !editable ? (
        renderView ? (
          renderView(value)
        ) : (
          <>
            {label && <Typography
              variant="caption"
              component="div"
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                lineHeight: 1.66,
                mb: 0.5
              }}
            >
              {label}
            </Typography>}
            <Typography variant="body1">
              {value ? t('yes') : t('no')}
            </Typography>
          </>
        )
      ) : (
        <ErrorWrapper message={validatorResult} showMessage={showError}
        sx={{
          position:"relative"
        }}>
          {/* Label above the switch, adjusted position to align with others */}
          {label && <Typography
            variant="caption"
            component="div"
            sx={{
              position:"absolute",
              top:"-8px",
              left:"16px",
              color: 'text.secondary',
              fontSize: '0.7rem', // Consistent with SmartTextInput's label variant="caption"
              lineHeight:"1.4375em",
              fontWeight: 400

              // mb: 0, // Small margin below the label
              // ml: 1.75 // This matches the indentation of other TextField labels
            }}
          >
            {label}
          </Typography>}
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(value)}
                onChange={handleChange}
                sx={switchSx}
                {...props}
              />
            }
            label={value ? (trueValueLabel || t('main:on')) : (falseValueLabel || t('main:off'))}
            sx={{ mt: 0, ml: 0.5, py:0.5}} // Adjusted margin-left to align with the switch itself
          />
          {/* Helper Text below the switch and label */}
          {helperText && (
            <FormHelperText
              sx={{
                ml: 1.75, // Align helper text with the switch or label
                mt: -0.5, // Adjust top margin to pull it closer to the switch
                color: 'text.secondary' // Ensure it's themed as helper text
              }}
            >
              {helperText}
            </FormHelperText>
          )}
        </ErrorWrapper>
      )}
    </Box>
  );
};

export default SmartSwitchInput;